const SESSION_URL = 'https://9gyb2coj2i.execute-api.eu-west-2.amazonaws.com/dev/create-stripe-connect-account-session';
const RETRY_DELAYS = [2000, 4000];
const REQUEST_TIMEOUT = 20000;

const interrupted = () => new DOMException('Payout setup interrupted', 'AbortError');

const wait = (delay, signal) => new Promise((resolve, reject) => {
    const abort = () => { clearTimeout(timer); signal.removeEventListener('abort', abort); reject(interrupted()); };
    const timer = setTimeout(() => {
        signal.removeEventListener('abort', abort);
        resolve();
    }, delay);
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) abort();
});

export async function createOnboardingSession({ getIdToken, address, signal, onRetry }) {
    const body = JSON.stringify({ address: address || null });
    for (let attempt = 0; ; attempt += 1) {
        if (signal.aborted) throw interrupted();
        const idToken = await getIdToken();
        if (signal.aborted) throw interrupted();
        const controller = new AbortController();
        const abort = () => controller.abort();
        signal.addEventListener('abort', abort, { once: true });
        const timeout = setTimeout(abort, REQUEST_TIMEOUT);
        try {
            const response = await fetch(SESSION_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
                body,
                signal: controller.signal,
            });
            if (!response.ok) {
                const message = response.status === 401 ? 'Your session has expired. Please log in again.'
                    : response.status === 403 ? 'Only photographers can set up payouts.'
                        : 'We couldn’t start payout setup. Please try again.';
                throw Object.assign(new Error(message), {
                    retryable: response.status === 408 || (response.status >= 500 && response.status < 600),
                });
            }
            const data = await response.json();
            if (typeof data.clientSecret !== 'string' || !data.clientSecret.trim()) {
                throw new Error('We couldn’t load the payout setup session. Please try again.');
            }
            if (signal.aborted) throw interrupted();
            return data.clientSecret;
        } catch (error) {
            if (signal.aborted) throw interrupted();
            if (!(error.retryable || error.name === 'TypeError' || error.name === 'AbortError') || attempt >= RETRY_DELAYS.length) {
                throw new Error(error.retryable === false || error.name === 'Error'
                    ? error.message : 'We couldn’t reach payout setup. Please try again.');
            }
        } finally {
            clearTimeout(timeout);
            signal.removeEventListener('abort', abort);
        }
        onRetry?.();
        await wait(RETRY_DELAYS[attempt], signal);
    }
}
